import { useMemo, useState } from 'react';
import { Box, Chip, Popover, Stack, Typography } from '@mui/material';

export interface EssayAnnotation {
  id: string;
  type: string;
  quote: string;
  comment: string;
  suggestion?: string;
  /** Grading dimension this note supports (內容/表達/結構/標點字體) */
  dimension?: string;
}

type Group = 'praise' | 'issue' | 'advice';

/**
 * 眉批 vocabulary. Hue carries the group (praise green, error red, advice blue)
 * so a teacher reads the balance of a script at a glance; the label carries the
 * specific kind, because 病句 and 冗贅 need different fixes.
 */
const TYPE_STYLE: Record<string, { label: string; group: Group; color: string; bg: string }> = {
  // 優點
  excellent: { label: '佳句', group: 'praise', color: '#1B5E20', bg: 'rgba(27,94,32,0.16)' },
  rhetoric: { label: '修辭', group: 'praise', color: '#2E7D32', bg: 'rgba(46,125,50,0.15)' },
  echo: { label: '呼應', group: 'praise', color: '#00695C', bg: 'rgba(0,105,92,0.15)' },
  insight: { label: '立意', group: 'praise', color: '#33691E', bg: 'rgba(51,105,30,0.15)' },
  // 錯誤
  typo: { label: '錯別字', group: 'issue', color: '#B71C1C', bg: 'rgba(183,28,28,0.15)' },
  grammar: { label: '病句', group: 'issue', color: '#C62828', bg: 'rgba(198,40,40,0.15)' },
  punctuation: { label: '標點', group: 'issue', color: '#AD1457', bg: 'rgba(173,20,87,0.14)' },
  diction: { label: '用詞', group: 'issue', color: '#D84315', bg: 'rgba(216,67,21,0.15)' },
  // 建議
  concretize: { label: '具體化', group: 'advice', color: '#1565C0', bg: 'rgba(21,101,192,0.14)' },
  deepen: { label: '深化', group: 'advice', color: '#4527A0', bg: 'rgba(69,39,160,0.14)' },
  trim: { label: '精簡', group: 'advice', color: '#00838F', bg: 'rgba(0,131,143,0.14)' },
  transition: { label: '銜接', group: 'advice', color: '#6A1B9A', bg: 'rgba(106,27,154,0.14)' },
  ontopic: { label: '扣題', group: 'advice', color: '#EF6C00', bg: 'rgba(239,108,0,0.16)' },
};
const FALLBACK_STYLE = { label: '眉批', group: 'advice' as Group, color: '#455A64', bg: 'rgba(69,90,100,0.14)' };
const styleOf = (t: string) => TYPE_STYLE[t] ?? FALLBACK_STYLE;

const GROUP_LABEL: Record<Group, string> = { praise: '優點', issue: '錯誤', advice: '改進建議' };

interface Segment {
  text: string;
  ann?: EssayAnnotation;
}

/**
 * OCR emits one newline per answer-grid line (~18 chars), so rendering the raw
 * text with pre-wrap re-creates the grid and the paragraph never uses the
 * available width. Collapse those soft breaks; keep blank-line breaks, which are
 * the student's real paragraphs.
 *
 * Applied to the annotation quotes too, so spans still match after reflowing.
 */
function reflow(text: string): string {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\n[ \t]*\n+/g, '\u0000') // protect real paragraph breaks
    .replace(/[ \t]*\n[ \t]*/g, (_m, offset: number, whole: string) => {
      // join without a space for CJK; keep one between Latin words
      const before = whole[offset - 1] ?? '';
      const after = whole[offset + _m.length] ?? '';
      return /[A-Za-z0-9]/.test(before) && /[A-Za-z0-9]/.test(after) ? ' ' : '';
    })
    // NUL is an internal sentinel for paragraph breaks, never present in real
    // transcript text.
    // eslint-disable-next-line no-control-regex
    .replace(/\u0000/g, '\n\n');
}

/**
 * Split the transcript on annotation quotes so each quoted span can be
 * highlighted in place. Quotes are matched verbatim (the grader guarantees this
 * and drops any that don't occur); longest-first so a short quote nested inside
 * a longer one can't split it.
 */
function segment(text: string, annotations: EssayAnnotation[]): Segment[] {
  const ordered = [...annotations].sort((a, b) => b.quote.length - a.quote.length);
  let segments: Segment[] = [{ text }];

  for (const ann of ordered) {
    const next: Segment[] = [];
    for (const seg of segments) {
      if (seg.ann || !seg.text.includes(ann.quote)) {
        next.push(seg);
        continue;
      }
      const idx = seg.text.indexOf(ann.quote);
      if (idx > 0) next.push({ text: seg.text.slice(0, idx) });
      next.push({ text: ann.quote, ann });
      const rest = seg.text.slice(idx + ann.quote.length);
      if (rest) next.push({ text: rest });
    }
    segments = next;
  }
  return segments;
}

export function AnnotatedTranscript({
  text,
  annotations = [],
}: {
  text: string;
  annotations?: EssayAnnotation[];
}) {
  const [anchor, setAnchor] = useState<{ el: HTMLElement; ann: EssayAnnotation } | null>(null);
  const segments = useMemo(() => {
    const flowed = reflow(text);
    const flowedAnns = annotations.map((a) => ({ ...a, quote: reflow(a.quote) }));
    return segment(flowed, flowedAnns);
  }, [text, annotations]);

  if (!text) return <Typography variant="body2">（無作答）</Typography>;

  return (
    <>
      <Typography
        variant="body2"
        component="div"
        sx={{ lineHeight: 1.9, whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'justify' }}
      >
        {segments.map((seg, i) =>
          seg.ann ? (
            <Box
              key={i}
              component="mark"
              onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor({ el: e.currentTarget, ann: seg.ann! })}
              sx={{
                bgcolor: styleOf(seg.ann.type).bg,
                color: 'inherit',
                borderBottom: '2px solid',
                borderColor: styleOf(seg.ann.type).color,
                borderRadius: '2px',
                px: 0.25,
                cursor: 'pointer',
                '&:hover': { filter: 'brightness(0.94)' },
              }}
            >
              {seg.text}
            </Box>
          ) : (
            <span key={i}>{seg.text}</span>
          ),
        )}
      </Typography>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor?.el ?? null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { maxWidth: 380, p: 1.5 } } }}
      >
        {anchor && (
          <Stack spacing={1}>
            <Chip
              size="small"
              label={styleOf(anchor.ann.type).label}
              sx={{ alignSelf: 'flex-start', bgcolor: styleOf(anchor.ann.type).bg, color: styleOf(anchor.ann.type).color, fontWeight: 600 }}
            />
            {anchor.ann.dimension && (
              <Typography variant="caption" color="text.secondary">
                對應維度：{anchor.ann.dimension}
              </Typography>
            )}
            <Typography variant="body2">{anchor.ann.comment}</Typography>
            {anchor.ann.suggestion && (
              <Box sx={{ borderLeft: '3px solid', borderColor: 'divider', pl: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  建議改寫
                </Typography>
                <Typography variant="body2">{anchor.ann.suggestion}</Typography>
              </Box>
            )}
          </Stack>
        )}
      </Popover>
    </>
  );
}

/** Grouped, filterable list — a teacher can read all 錯誤 at once, or all 建議. */
export function AnnotationList({ annotations }: { annotations: EssayAnnotation[] }) {
  const [filter, setFilter] = useState<Group | 'all'>('all');
  if (!annotations.length) return null;

  const counts = annotations.reduce<Record<string, number>>((acc, a) => {
    const g = styleOf(a.type).group;
    acc[g] = (acc[g] ?? 0) + 1;
    return acc;
  }, {});
  const shown = filter === 'all' ? annotations : annotations.filter((a) => styleOf(a.type).group === filter);
  const groups: Group[] = ['praise', 'issue', 'advice'];

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          label={`全部 ${annotations.length}`}
          onClick={() => setFilter('all')}
          variant={filter === 'all' ? 'filled' : 'outlined'}
        />
        {groups.map((g) =>
          counts[g] ? (
            <Chip
              key={g}
              size="small"
              label={`${GROUP_LABEL[g]} ${counts[g]}`}
              onClick={() => setFilter(g)}
              variant={filter === g ? 'filled' : 'outlined'}
              sx={
                filter === g
                  ? { bgcolor: styleOf(g === 'praise' ? 'excellent' : g === 'issue' ? 'grammar' : 'deepen').bg,
                      color: styleOf(g === 'praise' ? 'excellent' : g === 'issue' ? 'grammar' : 'deepen').color,
                      fontWeight: 600 }
                  : undefined
              }
            />
          ) : null,
        )}
      </Stack>

      {shown.map((a) => {
        const st = styleOf(a.type);
        return (
          <Box key={a.id} sx={{ borderLeft: '3px solid', borderColor: st.color, pl: 1 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.25 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                label={st.label}
                sx={{ height: 18, fontSize: 11, bgcolor: st.bg, color: st.color, fontWeight: 600 }}
              />
              {a.dimension && (
                <Chip size="small" variant="outlined" label={a.dimension} sx={{ height: 18, fontSize: 11 }} />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }} noWrap>
                「{a.quote.replace(/\s+/g, '')}」
              </Typography>
            </Stack>
            <Typography variant="body2">{a.comment}</Typography>
            {a.suggestion && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                建議：{a.suggestion}
              </Typography>
            )}
          </Box>
        );
      })}
    </Stack>
  );
}
